import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
    Alert, Box, Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
    MenuItem,
} from '@mui/material';
import { FileDownload as FileDownloadIcon } from '@mui/icons-material';
import AccountAutocomplete from '../common/AccountAutocomplete';
import { Account, DayBook as DayBookData, DayBookFilters } from '../../interfaces/accounting.interface';
import { AccountService } from '../../services/account.service';
import { CashFlowService } from '../../services/cashFlow.service';
import { useToast } from '../../context/ToastContext';
import { downloadCsv, sanitizeForSpreadsheet as safe } from '../../utils/csv';
import { saveBlob } from '../../utils/saveBlob';
import { formatMoney } from '../../utils/format';
import { formatDate, toLocalIsoDate } from '../../utils/dateUtils';
import { referenceLabel } from '../../utils/journalEntryText';

const num = { textAlign: 'right' as const, whiteSpace: 'nowrap' as const };

export const truncationNotice = (entries: number): string =>
    `Export cut at ${entries.toLocaleString('en-US')} entries \u2014 totals are partial`;

export const dayBookCsv = (r: DayBookData): { headers: string[]; rows: (string | number)[][] } => {
    const rows: (string | number)[][] = [];
    r.days.forEach(d => {
        d.entries.forEach(e => e.lines.forEach(l => rows.push([
            formatDate(e.date), safe(e.entryNumber), safe(e.referenceType ?? 'MANUAL'), safe(l.accountCode),
            safe(l.accountName), safe(l.description ?? e.description ?? ''), l.debit || '', l.credit || '',
        ])));
        rows.push([formatDate(d.date), '', '', '', '', 'Day total', d.totalDebit, d.totalCredit]);
    });
    if (r.truncated) rows.push(['', '', '', '', '', truncationNotice(r.entryCount), '', '']);
    rows.push(['', '', '', '', '', 'Grand total', r.totalDebit, r.totalCredit]);
    return { headers: ['Date', 'Entry no.', 'Source', 'Account code', 'Account', 'Narration', 'Debit', 'Credit'], rows };
};

const DayBook: React.FC = () => {
    const toast = useToast();
    const today = toLocalIsoDate();
    const [from, setFrom] = useState(today);
    const [to, setTo] = useState(today);
    const [referenceType, setReferenceType] = useState('');
    const [accountId, setAccountId] = useState('');
    const [search, setSearch] = useState('');
    const [accounts, setAccounts] = useState<Account[]>([]);
    const [sources, setSources] = useState<string[]>([]);
    const [report, setReport] = useState<DayBookData | null>(null);
    const [applied, setApplied] = useState<DayBookFilters | null>(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        AccountService.getAllAccounts(true)
            .then(a => setAccounts(Array.isArray(a) ? a : []))
            .catch(err => toast.showApiError(err, 'Failed to load accounts'));
        CashFlowService.getDayBookReferenceTypes()
            .then(setSources)
            .catch(err => toast.showApiError(err, 'Failed to load sources'));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const load = useCallback(async () => {
        const filters: DayBookFilters = { from, to, referenceType, accountId, search: search.trim() };
        try {
            setLoading(true);
            setReport(await CashFlowService.getDayBook(filters));
            setApplied(filters);
        } catch (err) {
            toast.showApiError(err, 'Failed to load day book');
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [from, to, referenceType, accountId, search]);

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Exports use the APPLIED filters / report on screen, never the unapplied inputs.
    const exportCsv = () => {
        if (!report) return;
        const { headers, rows } = dayBookCsv(report);
        downloadCsv(`day-book-${report.fromDate}-to-${report.toDate}.csv`, headers, rows);
    };
    const exportXlsx = async () => {
        if (!applied) return;
        try {
            saveBlob(await CashFlowService.downloadDayBookXlsx(applied), `day-book-${applied.from}-to-${applied.to}.xlsx`);
        } catch (err) {
            toast.showApiError(err, 'Failed to export day book');
        }
    };

    return (
        <Box data-help-page="accounting.dayBook.page" sx={{ p: 2.5 }}>
            <Typography variant="h5" sx={{ mb: 2 }}>Day Book</Typography>
            <Box data-help-section="accounting.dayBook.filters" sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', mb: 3 }}>
                <TextField data-help="accounting.dayBook.from" label="From" type="date" size="small" value={from} onChange={e => setFrom(e.target.value)}
                    InputLabelProps={{ shrink: true }} sx={{ flex: '1 1 150px', maxWidth: 190 }} />
                <TextField data-help="accounting.dayBook.to" label="To" type="date" size="small" value={to} onChange={e => setTo(e.target.value)}
                    InputLabelProps={{ shrink: true }} sx={{ flex: '1 1 150px', maxWidth: 190 }} />
                <TextField data-help="accounting.dayBook.source" select label="Source" size="small" value={referenceType}
                    onChange={e => setReferenceType(e.target.value)} sx={{ flex: '1 1 180px', maxWidth: 240 }}>
                    <MenuItem value="">All sources</MenuItem>
                    {sources.map(s => <MenuItem key={s} value={s}>{s === 'MANUAL' ? 'Manual entry' : referenceLabel(s)}</MenuItem>)}
                </TextField>
                <AccountAutocomplete data-help="accounting.dayBook.account"
                    accounts={accounts} value={accountId} onChange={setAccountId} label="Account"
                    placeholder="All accounts" size="small" fullWidth={false}
                    data-testid="day-book-account"
                />
                <TextField data-help="accounting.dayBook.search" label="Entry no. or narration" size="small" value={search} onChange={e => setSearch(e.target.value)}
                    sx={{ flex: '1 1 160px', maxWidth: 220 }} />
                <Button data-help="accounting.dayBook.apply" variant="contained" onClick={load} disabled={loading}>{loading ? 'Loading...' : 'Apply'}</Button>
                <Button data-help="accounting.dayBook.exportCsv" variant="outlined" startIcon={<FileDownloadIcon />} onClick={exportCsv} disabled={loading || !report}>
                    Export CSV
                </Button>
                <Button data-help="accounting.dayBook.exportXlsx" variant="outlined" startIcon={<FileDownloadIcon />} onClick={exportXlsx} disabled={loading || !applied}>
                    Export XLSX
                </Button>
            </Box>

            {report?.truncated && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    {truncationNotice(report.entryCount)}. Narrow the date range or filters to see the rest.
                </Alert>
            )}
            {report && report.days.length === 0 && !loading && (
                <Typography color="text.secondary">No posted entries match these filters.</Typography>
            )}

            {report?.days.map(day => (
                <Paper key={day.date} variant="outlined" sx={{ mb: 2 }}>
                    <Typography sx={{ p: 1.5, fontWeight: 700 }}>{formatDate(day.date)}</Typography>
                    <TableContainer>
                        <Table data-help-section="accounting.dayBook.table" size="small" sx={{ minWidth: 640 }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell data-help="accounting.dayBook.col.account">Account</TableCell>
                                    <TableCell data-help="accounting.dayBook.col.narration">Narration</TableCell>
                                    <TableCell data-help="accounting.dayBook.col.debit" sx={num}>Debit</TableCell>
                                    <TableCell data-help="accounting.dayBook.col.credit" sx={num}>Credit</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {day.entries.map(e => (
                                    <React.Fragment key={e.id}>
                                        <TableRow sx={{ bgcolor: 'action.hover' }}>
                                            <TableCell colSpan={4}>
                                                <RouterLink to={`/accounting/journal-entries/${e.id}`}>{e.entryNumber}</RouterLink>
                                                {' '}· {e.referenceType ? referenceLabel(e.referenceType) : 'Manual entry'}
                                                {e.description ? ` · ${e.description}` : ''}
                                            </TableCell>
                                        </TableRow>
                                        {e.lines.map((l, i) => (
                                            <TableRow key={`${e.id}-${i}`}>
                                                <TableCell sx={{ pl: l.credit ? 5 : 2 }}>{l.accountCode} {l.accountName}</TableCell>
                                                <TableCell>{l.description}</TableCell>
                                                <TableCell sx={num}>{l.debit ? formatMoney(l.debit) : ''}</TableCell>
                                                <TableCell sx={num}>{l.credit ? formatMoney(l.credit) : ''}</TableCell>
                                            </TableRow>
                                        ))}
                                        <TableRow>
                                            <TableCell colSpan={2} sx={{ textAlign: 'right', color: 'text.secondary' }}>Entry total</TableCell>
                                            <TableCell sx={{ ...num, color: 'text.secondary' }}>{formatMoney(e.totalDebit)}</TableCell>
                                            <TableCell sx={{ ...num, color: 'text.secondary' }}>{formatMoney(e.totalCredit)}</TableCell>
                                        </TableRow>
                                    </React.Fragment>
                                ))}
                                <TableRow>
                                    <TableCell colSpan={2} sx={{ fontWeight: 700 }}>Day total</TableCell>
                                    <TableCell sx={{ ...num, fontWeight: 700 }}>{formatMoney(day.totalDebit)}</TableCell>
                                    <TableCell sx={{ ...num, fontWeight: 700 }}>{formatMoney(day.totalCredit)}</TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            ))}

            {report && report.days.length > 0 && (
                <Paper variant="outlined" sx={{ p: 1.5, display: 'flex', flexWrap: 'wrap', gap: 3, fontWeight: 700 }}>
                    <span>{report.entryCount} entries</span>
                    <span>Total debit {formatMoney(report.totalDebit)}</span>
                    <span>Total credit {formatMoney(report.totalCredit)}</span>
                </Paper>
            )}
        </Box>
    );
};

export default DayBook;
